import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';

/**
 * Whether the office is looking at a multi-location parent's overview rather
 * than one location — the web's counterpart of mobile-app's `isViewingParentOffice`.
 *
 * True on either signal:
 * - **Legacy** (`useIsParentOfficeSelector`): the account has child offices and
 *   the parent itself is the selected office. Under the DSO structure this is
 *   the web's "All locations" view — `LocationMenu` and the sidebar select the
 *   parent's own id there, so `selectedOfficeId` is set and the DSO signal alone
 *   would miss it.
 * - **DSO structure on** (GrowthBook `use-office-service`): a parent that has not
 *   picked a location has no `selectedOfficeId`. Off, a single-location office
 *   has none either, so this signal only counts with the structure on.
 *
 * A single-location office reads false either way: it has no child offices, and
 * under DSO its own id is the selected one. This is the formula
 * `OfficeMissingInfoGate` already uses inline; `Profile.js`, `NotAllowedGuard`
 * and `Dashboard.tsx` still carry their own variants of the rule.
 */
export const useIsViewingParentOffice = (): boolean => {
  const isLegacyParentSelected = useIsParentOfficeSelector();
  const hasNoSelectedOffice = useAppSelector((state) => !state.user.selectedOfficeId);

  return isLegacyParentSelected || (shouldUseDsoStructure() && hasNoSelectedOffice);
};
